import { useId, useLayoutEffect, useState, type ReactNode } from 'react'
import type { RubricSection, RubricLayout } from '@/lib/types'
import { Icon } from './Icon'
import './rubric.css'

/** Shared navigation for authored criteria and immutable scientific grading checks. */
export function RubricOutline({ sections, text, children, actions, header, editable = false, sectionProps, searchQuery, onSearchChange, collapsible = true, bulkToggles = true, onVisibilityChange, revealRequest }: {
  sections: RubricLayout
  text: (id: string) => string
  children: (section: RubricSection, ids: string[]) => ReactNode
  actions?: ReactNode
  header?: (section: RubricSection, index: number, controls: React.ButtonHTMLAttributes<HTMLButtonElement>) => ReactNode
  searchQuery?: string
  onSearchChange?: (query: string) => void
  collapsible?: boolean
  bulkToggles?: boolean
  onVisibilityChange?: () => void
  /** Pass a fresh request for each navigation, including repeated visits to one criterion. */
  revealRequest?: { criterionId: string }
  editable?: boolean
  sectionProps?: (section: RubricSection) => React.HTMLAttributes<HTMLElement>
}) {
  const prefix = useId().replace(/:/g, '')
  const [localQuery, setLocalQuery] = useState('')
  const query = searchQuery ?? localQuery
  const setQuery = onSearchChange ?? setLocalQuery
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const revealSection = revealRequest && sections.find(s => s.criterion_ids.includes(revealRequest.criterionId))?.section_id
  useLayoutEffect(() => {
    if (!revealRequest || !revealSection) return
    setQuery('')
    setCollapsed(current => {
      if (!current.has(revealSection)) return current
      const next = new Set(current)
      next.delete(revealSection)
      return next
    })
  }, [revealRequest, revealSection, setQuery])
  const count = sections.reduce((n, section) => n + section.criterion_ids.length, 0)
  const grouped = sections.length > 1 || sections.some(s => s.title)
  const named = sections.filter(s => s.section_id !== 'unsectioned').length
  const search = query.trim().toLocaleLowerCase()
  useLayoutEffect(() => { onVisibilityChange?.() }, [collapsed, search, onVisibilityChange])
  const toggle = (id: string) => setCollapsed(current => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const matches = (s: RubricSection) => s.criterion_ids.filter(id => !search || `${s.title} ${id} ${text(id)}`.toLocaleLowerCase().includes(search))
  const found = sections.reduce((n, s) => n + matches(s).length, 0)
  return <div className="rubric-organiser">
    {(editable || grouped || count >= 8) && <div className="rubric-toolbar">
      <label className="rubric-search"><Icon name="search" size={16} /><span className="visually-hidden">Find a criterion</span><input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a criterion…" /></label>
      {actions}
      {grouped && collapsible && bulkToggles && <button type="button" className="rubric-icon-button" aria-label="Expand all" title="Expand all sections" onClick={() => setCollapsed(new Set())}><Icon name="expand" size={15} /></button>}
      {grouped && collapsible && bulkToggles && <button type="button" className="rubric-icon-button" aria-label="Collapse all" title="Collapse all sections" onClick={() => setCollapsed(new Set(sections.map(s => s.section_id)))}><Icon name="list" size={15} /></button>}
    </div>}
    <p className="rubric-count muted small" role="status">{search ? `${found} of ${count} criteria match` : `${count} ${count === 1 ? 'criterion' : 'criteria'}${grouped ? ` · ${named} ${named === 1 ? 'section' : 'sections'}` : ''}`}</p>
    {search && !found && <p className="empty">No criteria match. Try another word or criterion ID.</p>}
    {sections.map((section, index) => {
      const ids = matches(section)
      if (search && !ids.length) return null
      const open = !collapsible || !!search || !(grouped || header) || !collapsed.has(section.section_id)
      const controls: React.ButtonHTMLAttributes<HTMLButtonElement> = {
        disabled: !collapsible || !!search,
        title: search ? 'Clear search to collapse sections' : `${open ? 'Collapse' : 'Expand'} section`,
        'aria-expanded': open, 'aria-controls': `${prefix}-${section.section_id}-cards`,
        'aria-label': `${open ? 'Collapse' : 'Expand'} ${section.title || 'Unsectioned'}`,
        onClick: () => toggle(section.section_id),
      }
      return <section key={section.section_id} id={`${prefix}-${section.section_id}`} data-section-id={section.section_id} className={`rubric-section ${grouped ? 'is-grouped' : ''}`} aria-label={section.title || 'Unsectioned'} {...sectionProps?.(section)}>
        {(grouped || header) && <div className="rubric-section-heading" data-collapsible={collapsible && !search || undefined} onClick={e => {
          if (collapsible && !search && !(e.target as Element).closest('button, input, select, textarea, a, [data-rubric-grip]')) toggle(section.section_id)
        }}>
          {header ? header(section, index, controls) : <h3 aria-label={section.title || 'Unsectioned'}>{collapsible
            ? <button type="button" className="rubric-section-title" {...controls}>{section.title || 'Unsectioned'} <span className="muted small" aria-hidden="true">{section.criterion_ids.length}</span></button>
            : <>{section.title || 'Unsectioned'} <span className="muted small" aria-hidden="true">{section.criterion_ids.length}</span></>}</h3>}
        </div>}
        <div id={`${prefix}-${section.section_id}-cards`} hidden={!open}>{children(section, ids)}</div>
      </section>
    })}
  </div>
}
